import {FC} from "react"
import Markdown from "react-markdown"
import {ReactSVG} from "react-svg"

import infoIcon from "@frontend/design/icons/info-bold.svg"
import {getHostBridge} from "@frontend/hostContract"
import {useTranslation} from "@frontend/i18n"
import {brandLinks} from "@frontend/themes/brandLinks"
import {navigateToPart} from "@frontend/utils/appHostFunctions"
import {trackEvent} from "@frontend/utils/trackEvent"
import {useBooleanState} from "@frontend/utils/useBooleanState"
import {
  ConfirmedCompatiblePart,
  DiyStepCaution,
  MessageMedia,
  MessagePartImages,
  MessageVideo,
  QuestionDocument,
  isMessageVideo,
} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {hstack, vstack} from "@styled-system/patterns"

import {MediaCarousel} from "./MediaCarousel"
import {Media} from "./media"
import {normalizePartMpnKey} from "./partShortcodes"
import {stripInternalContext} from "./stripInternalContext"
import {CautionIcon} from "../causeOnMessages/[causeOnMessageId]/diy/components/caution-icon"

const isAllowedLinkUrl = (url: string): boolean =>
  !!brandLinks.allowedLinkUrlPrefixes.find(
    (allowedUrl: string) =>
      url.startsWith(allowedUrl) || url.startsWith(`${allowedUrl}/`),
  )

interface MessageContentProps {
  message: string
  isThinkingOrAi: boolean
  caseId?: string
  questionMedia?: MessageMedia[]
  helperMedia?: MessageMedia[]
  questionDocuments?: QuestionDocument[]
  disassemblyVideos?: MessageVideo[]
  partImages?: MessagePartImages
  /** Parts from this AI message used to resolve [[part:OEM]] shortcodes to navigateToPart. */
  linkableParts?: ConfirmedCompatiblePart[]
  onSelectMedia: (url: string) => void
  isAskModelNumberMessage?: boolean
  isWidget?: boolean
}

export const MessageContent: FC<MessageContentProps> = ({
  isThinkingOrAi,
  message,
  caseId,
  questionMedia = [],
  helperMedia = [],
  questionDocuments = [],
  disassemblyVideos = [],
  partImages,
  linkableParts,
  onSelectMedia,
  isAskModelNumberMessage,
  isWidget = false,
}) => {
  const t = useTranslation()
  const trackDocumentClick = (): void => {
    trackEvent(
      isWidget ? "widget_chat_diag_document_click" : "chat_diag_document_click",
    )
  }

  const hasMixedHelperMedia =
    helperMedia.some(isMessageVideo) &&
    helperMedia.some((media) => !isMessageVideo(media))

  const helperMediaDescription =
    helperMedia.length > 0
      ? t(
          hasMixedHelperMedia
            ? "conversation.questionMediaMixed"
            : isMessageVideo(helperMedia[0])
              ? "conversation.questionMediaVideo"
              : "conversation.questionMediaImage",
          {count: helperMedia.length},
        )
      : ""

  // Split question media into videos and non-video images so that
  // multiple videos can be shown in a snap-scroll carousel.
  const questionVideos = questionMedia.filter(isMessageVideo)
  const questionImages = questionMedia.filter((m) => !isMessageVideo(m))

  // Normalize literal "\n" sequences (backslash + n) that LLMs sometimes
  // produce in structured-output strings into real newline characters so
  // that Markdown / whitespace-based rendering works correctly.
  const normalizedMessage = stripInternalContext(message.replace(/\\n/g, "\n"))
    .replace(
      /\[(FIRE|ELECTRICITY|WATER|SHARP|HEAVY|HOT|FRAGILE|SPILL)\]/g,
      "![caution:$1]()",
    )
    .replace(/\[\[part:([^\]]+)\]\]/g, (_match, oem: string) => {
      const token = encodeURIComponent(String(oem).trim())
      return `![partlink:${token}]()`
    })

  if (isThinkingOrAi) {
    return (
      <>
        <div
          className={css({
            "& > * + *": {
              marginTop: 16,
            },
            "& strong": {
              fontWeight: "500",
            },
            "& li": {
              textStyle: "bodyLargeLight",
            },
            "& ol": {
              listStyleType: "decimal",
              paddingLeft: 24,
            },
            "& ol li": {
              paddingLeft: 8,
            },
            "& ul": {
              listStyleType: "disc",
              paddingLeft: 24,
            },
            "& ul li": {
              paddingLeft: 8,
            },
            "& p": {
              textStyle: "bodyLargeLight",
            },
          })}
        >
          <Markdown
            allowedElements={[
              "a",
              "em",
              "img",
              "li",
              "ol",
              "p",
              "strong",
              "ul",
            ]}
            components={{
              img: ({src, alt}) => {
                if (alt?.startsWith("caution:")) {
                  const caution = alt.split(":")[1] as DiyStepCaution
                  return (
                    <CautionIcon
                      caution={caution}
                      className={css({marginX: 4})}
                    />
                  )
                }
                if (alt?.startsWith("partlink:")) {
                  const encoded = alt.slice("partlink:".length)
                  let oem = encoded
                  try {
                    oem = decodeURIComponent(encoded)
                  } catch {
                    // use raw token
                  }
                  const key = normalizePartMpnKey(oem)
                  const part = linkableParts?.find(
                    (p) =>
                      normalizePartMpnKey(p.manufacturerPartNumber) === key,
                  )
                  if (!part) {
                    return <span>{oem}</span>
                  }
                  const label = part.manufacturerPartNumber
                  return (
                    <button
                      type="button"
                      className={css({
                        display: "inline",
                        padding: 0,
                        margin: 0,
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: "turquoise",
                        textDecoration: "underline",
                        font: "inherit",
                        textAlign: "inherit",
                        verticalAlign: "baseline",
                      })}
                      onClick={() => {
                        trackEvent(
                          isWidget
                            ? "widget_view_part_early"
                            : "chat_view_part",
                        )
                        navigateToPart(part, caseId ? {caseId} : undefined)
                      }}
                    >
                      {label}
                    </button>
                  )
                }
                return <img src={src} alt={alt} />
              },
              a: ({href, children}) =>
                href && isAllowedLinkUrl(href) ? (
                  <a
                    href={href}
                    target="_blank"
                    className={css({
                      color: "text.link",
                      textDecoration: "underline",
                      _hover: {
                        color: "text.link.hover",
                      },
                    })}
                    rel="noreferrer"
                    onClick={(e) => {
                      try {
                        const url = new URL(href)
                        if (url.origin === window.location.origin) {
                          const navigateTo = getHostBridge()?.navigateTo
                          if (navigateTo) {
                            e.preventDefault()
                            navigateTo(url.pathname + url.search + url.hash)
                          }
                        }
                      } catch {
                        // malformed href -- let default behavior handle it
                      }
                    }}
                  >
                    {children}
                  </a>
                ) : (
                  <span>{children}</span>
                ),
            }}
          >
            {normalizedMessage}
          </Markdown>
        </div>
        {questionMedia.length > 0 && (
          <div>
            {questionVideos.length > 1 ? (
              <div className={css({marginY: 15})}>
                <MediaCarousel
                  media={questionVideos}
                  onSelectMedia={onSelectMedia}
                  getSelectUrl={(m) =>
                    isMessageVideo(m) ? m.thumbnailUrl : m.url
                  }
                />
              </div>
            ) : (
              questionVideos.length === 1 && (
                <div
                  className={hstack({
                    flexWrap: "wrap",
                    gap: 15,
                    marginY: 15,
                  })}
                >
                  <Media
                    media={questionVideos[0]}
                    onClick={() =>
                      onSelectMedia(questionVideos[0].thumbnailUrl)
                    }
                    isWidget={isWidget}
                  />
                </div>
              )
            )}
            {questionImages.length > 0 && (
              <div
                className={hstack({
                  flexWrap: "wrap",
                  gap: 15,
                  marginY: 15,
                })}
              >
                {questionImages.map((media) => (
                  <Media
                    media={media}
                    onClick={() => onSelectMedia(media.url)}
                    key={media.url}
                    isWidget={isWidget}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {isAskModelNumberMessage && (
          <FindModelNumberInstructions
            messageMedia={helperMedia}
            onSelectMedia={onSelectMedia}
            questionMediaDescription={helperMediaDescription}
            isWidget={isWidget}
          />
        )}
        {disassemblyVideos.length > 0 && (
          <div>
            {t("conversation.disassemblyVideos", {
              count: disassemblyVideos.length,
            })}
            {disassemblyVideos.length > 1 ? (
              <div className={css({marginY: 15})}>
                <MediaCarousel
                  media={disassemblyVideos}
                  onSelectMedia={onSelectMedia}
                  getSelectUrl={(m) =>
                    isMessageVideo(m) ? m.thumbnailUrl : m.url
                  }
                />
              </div>
            ) : (
              <div
                className={hstack({
                  flexWrap: "wrap",
                  gap: 15,
                  marginY: 15,
                })}
              >
                <Media
                  media={disassemblyVideos[0]}
                  onClick={() =>
                    onSelectMedia(disassemblyVideos[0].thumbnailUrl)
                  }
                  isWidget={isWidget}
                />
              </div>
            )}
          </div>
        )}
        {partImages && partImages.images.length > 0 && (
          <div>
            {t("conversation.partImages", {
              partType: partImages.partType.toLowerCase(),
              count: partImages.images.length,
            })}
            <div
              className={hstack({
                flexWrap: "wrap",
                gap: 15,
                marginY: 15,
              })}
            >
              {partImages.images.map((image) => (
                <Media
                  media={image}
                  onClick={() => {
                    onSelectMedia(image.url)
                  }}
                  key={image.url}
                  isWidget={isWidget}
                />
              ))}
            </div>
          </div>
        )}
        {questionDocuments.length === 1 && (
          <button
            type="button"
            onClick={() => {
              trackDocumentClick()
              onSelectMedia(questionDocuments[0].url)
            }}
            className={css({
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 12,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "lineGrey",
              cursor: "pointer",
              textAlign: "left",
              color: "text.primary",
              width: "100%",
              "&:hover": {
                boxShadow:
                  "0px 9px 20px rgba(28, 37, 72, 0.14), 0px 3px 3px rgba(28, 37, 72, 0.06)",
              },
            })}
          >
            <img
              src={
                questionDocuments[0].thumbnailUrl ?? questionDocuments[0].url
              }
              alt={questionDocuments[0].name}
              className={css({
                width: "100%",
                height: "auto",
                maxHeight: 240,
                objectFit: "contain",
                backgroundColor: "lightGrey",
              })}
            />
            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: 4,
                padding: 12,
                borderTopWidth: 1,
                borderTopStyle: "solid",
                borderTopColor: "lineGrey",
                backgroundColor: "lightGrey",
              })}
            >
              <span
                className={css({
                  textStyle: "captionMedium",
                  textTransform: "capitalize",
                  color: "text.secondary",
                })}
              >
                {questionDocuments[0].category}
              </span>
              <span className={css({textStyle: "body2"})}>
                {questionDocuments[0].name}
              </span>
            </div>
          </button>
        )}
        {questionDocuments.length > 1 && (
          <div>
            {t("conversation.questionDocuments", {
              count: questionDocuments.length,
            })}
            <div
              className={hstack({
                flexWrap: "wrap",
                gap: 15,
                marginY: 15,
              })}
            >
              {questionDocuments.map((doc) => (
                <button
                  type="button"
                  onClick={() => {
                    trackDocumentClick()
                    onSelectMedia(doc.url)
                  }}
                  key={doc.url}
                  className={css({
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    padding: 12,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: "lineGrey",
                    backgroundColor: "lightGrey",
                    textAlign: "left",
                    cursor: "pointer",
                    color: "text.primary",
                    textStyle: "body2",
                    "&:hover": {
                      backgroundColor: "background.secondary",
                    },
                  })}
                >
                  <span
                    className={css({
                      textStyle: "captionMedium",
                      textTransform: "capitalize",
                      color: "text.secondary",
                    })}
                  >
                    {doc.category}
                  </span>
                  <span>{doc.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div className={vstack({gap: 16, alignItems: "flex-start"})}>
      <span
        className={css({
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          width: "100%",
          textStyle: "bodyLargeLight",
        })}
      >
        {normalizedMessage}
      </span>
      {questionMedia.length > 0 && (
        <div
          className={hstack({
            flexWrap: "wrap",
            gap: 15,
          })}
        >
          {questionMedia.map((media) => (
            <Media
              media={media}
              onClick={() => {
                onSelectMedia(
                  isMessageVideo(media) ? media.thumbnailUrl : media.url,
                )
              }}
              key={media.url}
              isWidget={isWidget}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const FindModelNumberInstructions: FC<{
  messageMedia: MessageMedia[]
  onSelectMedia: (url: string) => void
  questionMediaDescription: string
  isWidget?: boolean
}> = ({messageMedia, onSelectMedia, questionMediaDescription, isWidget}) => {
  const {
    state: isDisplayingInstructions,
    setTrue: displayInstructions,
    setFalse: hideInstructions,
  } = useBooleanState(false)
  const t = useTranslation()
  return (
    <>
      <div
        className={css({
          width: 79,
          borderTopWidth: 1,
          borderTopStyle: "dashed",
          borderTopColor: "border.brand.secondary",
        })}
      />
      <div
        className={hstack({
          alignItems: "center",
          paddingY: 10,
          paddingX: 12,
          width: "fit-content",
          gap: 4,
          minHeight: 36,
          borderRadius: 999,
          fontSize: 13,
          backgroundColor: "background.brand.primarySubtle",
          borderStyle: "solid",
          borderWidth: 1,
          borderColor: "border.brand.primary",
        })}
      >
        <ReactSVG
          src={infoIcon}
          className={
            isDisplayingInstructions
              ? css({
                  "& *": {
                    stroke: "black",
                  },
                })
              : ""
          }
        />
        <span
          className={css({
            textStyle: "captionRegular",
            color: "text.primary",
            paddingLeft: 2,
            paddingRight: 12,
          })}
        >
          {t(
            isDisplayingInstructions
              ? "conversation.modelNumberTip"
              : "conversation.whereIsMyModelNumber",
          )}
        </span>
        <button
          onClick={
            isDisplayingInstructions ? hideInstructions : displayInstructions
          }
          className={css({
            color: "text.brand.secondary",
            height: 20,
            paddingX: 2,
            textStyle: "captionMedium",
            alignItems: "center",
            cursor: "pointer",
          })}
        >
          {t(
            isDisplayingInstructions
              ? "conversation.hide"
              : "conversation.reveal",
          )}
        </button>
      </div>
      {isDisplayingInstructions && (
        <>
          <p
            className={css({
              color: "text.primary",
              textStyle: "bodyLight",
            })}
          >
            {t("conversation.modelNumberLocationInfo")}
          </p>
          {messageMedia.length > 0 && (
            <div
              className={css({
                color: "text.secondary",
                textStyle: "captionMedium",
              })}
            >
              {questionMediaDescription}
              <div
                className={hstack({
                  flexWrap: "wrap",
                  gap: 15,
                  marginY: 15,
                })}
              >
                {messageMedia.map((media) => (
                  <Media
                    media={media}
                    onClick={() => {
                      onSelectMedia(
                        isMessageVideo(media) ? media.thumbnailUrl : media.url,
                      )
                    }}
                    key={media.url}
                    isWidget={isWidget}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
